import { ForbiddenError } from '@ember-data/adapter/error';
import Route from '@ember/routing/route';
import { service } from '@ember/service';

import { isTesting, macroCondition } from '@embroider/macros';
import * as Sentry from '@sentry/ember';
import { forever } from 'ember-concurrency';
import window from 'ember-window-mock';

import {
  apiNamespace,
  companyCreationBaseURL,
  jsURL,
  registerJsURL,
} from 'register-partners-js/constants/hosts';
import { SUBMITTED_STATUSES } from 'register-partners-js/constants/registration-statuses';
import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import { ErrorInfo } from 'register-partners-js/utils/error-info';
import { fetchNavigationOrganizations } from 'register-partners-js/utils/fetch-navigation-organizations';

export default class RegistrationsRoute extends Route {
  mainLayout = MainLayout;

  @service deviceManager;
  @service router;
  @service segment;
  @service store;
  @service apiFlowManager;
  @service networkManager;

  async fetchCompanyCreationOrganization() {
    let url = `${companyCreationBaseURL}/${apiNamespace}/organizations`;
    let { data: ccOrganizations = [] } = await this.networkManager.request(url);
    return { ccOrganizations };
  }

  async model() {
    let [pendingPartnerRegistrations, registerOrganizations, appOrganizations] = await Promise.all([
      this.store.query('organization', {
        include: 'registration',
        filter: { registration_status_eq: 'pending' },
      }),
      this.store.findAll('register-organization'),
      this.loadAppOrganizations(),
    ]);

    let partnerRegistrationsViaRegister = registerOrganizations.filter(org =>
      Boolean(org.partnerRegistration.id)
    );

    // Drop submitted registrations so they're never auto-redirect or resume-page targets.
    let resolved = await Promise.all(
      partnerRegistrationsViaRegister.map(async org => ({
        org,
        reg: await org.partnerRegistration,
      }))
    );
    partnerRegistrationsViaRegister = resolved
      .filter(({ reg }) => !SUBMITTED_STATUSES.includes(reg?.status))
      .map(({ org }) => org);

    if (!this.apiFlowManager.isPartnersService) {
      registerOrganizations = registerOrganizations.filter(org => !org.partnerRegistration.id);
    }

    let registerOrganizationsWithPayment = registerOrganizations.filter(org =>
      Boolean(org.depositPaymentId)
    );

    // Navigation API only returns enabled organizations, no filtering needed
    let activeAppOrganizations = appOrganizations;

    let { ccOrganizations } = await this.fetchCompanyCreationOrganization();

    return {
      pendingPartnerRegistrations,
      partnerRegistrationsViaRegister,
      registerOrganizations,
      registerOrganizationsWithPayment,
      activeAppOrganizations,
      ccOrganizations: ccOrganizations.filter(org => org.relationships?.creation?.data?.id),
    };
  }

  async afterModel({
    pendingPartnerRegistrations,
    partnerRegistrationsViaRegister,
    registerOrganizations,
    registerOrganizationsWithPayment,
    activeAppOrganizations,
  }) {
    let currentRegistrationID =
      this.store.peekAll('redirection-token')[0]?.registrationID ??
      this.store.peekAll('register-redirection-token')[0]?.registrationID;

    // Token is the strongest signal of intent — always follow it
    if (currentRegistrationID) {
      return this.router.transitionTo('registration', currentRegistrationID);
    }

    let totalPartnerRegistrations =
      pendingPartnerRegistrations.length + partnerRegistrationsViaRegister.length;
    let hasPartnerRegistrations = Boolean(totalPartnerRegistrations);
    let hasRegisterOrganizations = Boolean(registerOrganizations.length);
    let hasRegisterOrganizationsWithPayment = Boolean(registerOrganizationsWithPayment.length);
    let activeOrgs = activeAppOrganizations.filter(org => !org.underRegistration);
    let capitalDepositOrgs = activeAppOrganizations.filter(org => org.underRegistration);

    // Exactly one partner registration and nothing else — continue without asking
    if (
      !hasRegisterOrganizations &&
      !activeOrgs.length &&
      !capitalDepositOrgs.length &&
      totalPartnerRegistrations === 1
    ) {
      let [organization] = pendingPartnerRegistrations.length
        ? pendingPartnerRegistrations
        : partnerRegistrationsViaRegister;

      let id = pendingPartnerRegistrations.length
        ? organization.registration.id
        : organization.partnerRegistration.id;

      return this.router.replaceWith('registration', id);
    }

    // No registrations, only fully active app orgs — user ended up here by mistake, redirect to app
    if (
      !hasPartnerRegistrations &&
      !hasRegisterOrganizations &&
      !capitalDepositOrgs.length &&
      activeOrgs.length
    ) {
      let [organization] = activeOrgs;
      if (this.deviceManager.isMobile || this.deviceManager.isWebview) {
        this.router.replaceWith('next-steps');
      } else {
        window.location.replace(`${jsURL}/organizations/${organization.slug}`);
      }

      if (macroCondition(!isTesting())) {
        await forever;
      }

      return;
    }

    // Only register orgs with no capital deposit payment — user ended up here by mistake, redirect to register-js
    if (
      !hasPartnerRegistrations &&
      !capitalDepositOrgs.length &&
      !hasRegisterOrganizationsWithPayment &&
      hasRegisterOrganizations
    ) {
      window.location.replace(`${registerJsURL}/start`);

      if (macroCondition(!isTesting())) {
        await forever;
      }

      return;
    }

    // All other mixes — show full resume list and let user choose
    this.segment.track('onboarding_partner_register_accounts_launcher', {
      is_mobile_browser: this.deviceManager.isMobile,
      is_webview: this.deviceManager.isWebview,
    });
  }

  async loadAppOrganizations() {
    try {
      return await fetchNavigationOrganizations(this.networkManager);
    } catch (error) {
      // similarly to qonto-register-js:
      // since the api returns 403 if no organizations are found,
      // we ignore this error and return empty array
      if (error instanceof ForbiddenError) {
        return [];
      }

      if (ErrorInfo.for(error).shouldSendToSentry) {
        Sentry.captureException(error);
      }
    }

    return [];
  }
}
